import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  Image
} from '@chakra-ui/react';
import { eventService } from '../../services/eventService';
import { useDataStore } from '../../contexts/DataStoreContext';
import { CreateEventForm } from './CreateEventForm';
import { EventManagement } from './EventManagement';
import { EventAnalytics } from './EventAnalytics';
import { FighterManagement } from './FighterManagement';

export const AdminDashboard = () => {
  const { getAllEvents, getEnrichedEvent, loading, error } = useDataStore();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { isOpen: isCreateOrEditEventModalOpen, onOpen: onCreateOrEditEventModalOpen, onClose: onCreateOrEditEventModalClose } = useDisclosure();
  const {
    isOpen: isDeleteEventModalOpen,
    onOpen: onDeleteEventModalOpen,
    onClose: onDeleteEventModalClose
  } = useDisclosure();
  const toast = useToast();

  // Events come from DataStore now - no need to load separately

  const handleEventCreated = async (eventId) => {
    onCreateOrEditEventModalClose();
    // DataStore will automatically update via real-time subscription
    const newEvent = getEnrichedEvent(eventId);
    if (newEvent) {
      setSelectedEvent(newEvent);
    }
  };

  const handleEventUpdate = async () => {
    // DataStore will automatically update via real-time subscription
    if (selectedEvent) {
      const updatedEvent = getEnrichedEvent(selectedEvent.id);
      if (updatedEvent) {
        setSelectedEvent(updatedEvent);
      }
    }
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    onDeleteEventModalOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    setDeleting(true);
    try {
      await eventService.deleteEvent(eventToDelete.id);

      toast({
        title: 'Event deleted successfully',
        description: `${eventToDelete.name} and all related data have been removed.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });

      // If we're viewing the deleted event, go back to list
      if (selectedEvent?.id === eventToDelete.id) {
        setSelectedEvent(null);
      }

      // DataStore will automatically update via real-time subscription

      // Close modal and clear state
      onDeleteEventModalClose();
      setEventToDelete(null);
    } catch (err) {
      toast({
        title: 'Failed to delete event',
        description: err.message || 'An error occurred while deleting the event.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading.events) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
        <Text mt={4}>Loading events...</Text>
      </Box>
    );
  }

  if (selectedEvent) {
    return (
      <Box p={6}>
        <HStack mb={6} justify="space-between">
          <Button variant="ghost" onClick={() => setSelectedEvent(null)}>
            ← Back to Events
          </Button>
          <Button
            colorScheme="red"
            variant="outline"
            onClick={() => handleDeleteClick(selectedEvent)}
          >
            Delete Event
          </Button>
        </HStack>

        <Tabs colorScheme="blue" variant="enclosed">
          <TabList>
            <Tab>Event Management</Tab>
            <Tab>Analytics</Tab>
          </TabList>

          <TabPanels>
            <TabPanel px={0}>
              <EventManagement event={selectedEvent} onEventUpdate={handleEventUpdate} />
            </TabPanel>
            <TabPanel px={0}>
              <EventAnalytics event={selectedEvent} />
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Delete Confirmation Modal - Rendered here when viewing event */}
        <Modal isOpen={isDeleteEventModalOpen} onClose={onDeleteEventModalClose} isCentered>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Delete Event</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack align="start" spacing={4}>
                <Text fontSize="md" color="white">
                  Are you sure you want to delete{' '}
                  <Text as="span" fontWeight="bold" color="white">
                    {eventToDelete?.name}
                  </Text>
                  ?
                </Text>
                <Alert status="warning" borderRadius="md" bg="orange.900" borderColor="orange.600" borderWidth="1px">
                  <AlertIcon color="orange.400" />
                  <VStack align="start" spacing={2} ml={2}>
                    <Text fontWeight="semibold" color="orange.100">
                      This action cannot be undone.
                    </Text>
                    <Text fontSize="sm" color="orange.200" fontWeight="medium">
                      This will permanently delete:
                    </Text>
                    <VStack align="start" spacing={1} pl={2}>
                      <Text fontSize="sm" color="orange.100">
                        • The event and all its details
                      </Text>
                      <Text fontSize="sm" color="orange.100">
                        • All {eventToDelete?.fights?.length || 0} fight(s)
                      </Text>
                      <Text fontSize="sm" color="orange.100">
                        • All user votes and predictions
                      </Text>
                    </VStack>
                  </VStack>
                </Alert>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onDeleteEventModalClose} isDisabled={deleting}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                isLoading={deleting}
                loadingText="Deleting..."
              >
                Delete Event
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    );
  }

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      <Heading mb={6}>Admin Dashboard</Heading>

      {error && (
        <Alert status="error" mb={6}>
          <AlertIcon />
          {error}
        </Alert>
      )}

      <Tabs colorScheme="blue" variant="enclosed">
        <TabList>
          <Tab>Events</Tab>
          <Tab>Fighters</Tab>
        </TabList>

        <TabPanels>
          {/* Events Tab */}
          <TabPanel px={0}>
            <HStack justify="space-between" mb={6}>
              <Heading size="lg">Event Management</Heading>
              <Button colorScheme="blue" onClick={onCreateOrEditEventModalOpen}>
                Create New Event
              </Button>
            </HStack>

            <VStack spacing={4} align="stretch">
              {getAllEvents().map(event => {
                const enrichedEvent = getEnrichedEvent(event.id);
                return (
                  <Card
                    key={event.id}
                    cursor="pointer"
                    _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                    transition="all 0.2s"
                    overflow="hidden"
                    onClick={() => setSelectedEvent(enrichedEvent || event)}
                  >
                    {/* Cover Image */}
                    {event.coverImage && (
                      <Box>
                        <Image
                          src={event.coverImage}
                          alt={`${event.name} cover image`}
                          width="100%"
                          height="150px"
                          objectFit="cover"
                        />
                      </Box>
                    )}
                    <CardHeader>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Heading size="md">{event.name}</Heading>
                          <Text>{event.location}</Text>
                          <Text color="gray.400">
                            {event.date instanceof Date
                              ? event.date.toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit'
                              })
                              : 'Date TBD'
                            }
                          </Text>
                        </VStack>
                        <VStack align="end" spacing={2}>
                          <Badge colorScheme={event.isActive ? 'green' : 'red'}>
                            {event.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                          <Text fontSize="sm" color="gray.400">
                            {enrichedEvent?.fights?.length || 0} fights
                          </Text>
                        </VStack>
                      </HStack>
                    </CardHeader>
                    <CardBody pt={0}>
                      <HStack justify="space-between">
                        <Text fontSize="sm" noOfLines={2}>
                          {event.description || 'No description'}
                        </Text>
                        <Button
                          size="sm"
                          colorScheme="blue"
                        >
                          Manage
                        </Button>
                      </HStack>
                    </CardBody>
                  </Card>
                );
              })}

              {getAllEvents().length === 0 && (
                <Card>
                  <CardBody textAlign="center" py={10}>
                    <Text color="gray.400" mb={4}>
                      No events created yet
                    </Text>
                    <Button colorScheme="blue" onClick={onCreateOrEditEventModalOpen}>
                      Create Your First Event
                    </Button>
                  </CardBody>
                </Card>
              )}
            </VStack>
          </TabPanel>

          {/* Fighters Tab */}
          <TabPanel px={0}>
            <FighterManagement />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <Modal isOpen={isCreateOrEditEventModalOpen} onClose={onCreateOrEditEventModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Event</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <CreateEventForm onEventCreated={handleEventCreated} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
